// Lightweight, privacy-friendly analytics client for the Slammed funnel.
// Sends anonymous events (page views, clicks, funnel steps) + a heartbeat that
// powers the admin "who's live now" view. No PII is collected.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${BACKEND_URL}/api/analytics/collect`;

const VISITOR_KEY = "sl_visitor_id";
const SESSION_KEY = "sl_session_id";
const ORIGIN_KEY = "sl_origin_area";

const uid = () =>
  (crypto?.randomUUID?.() ||
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);

const getVisitorId = () => {
  try {
    let v = localStorage.getItem(VISITOR_KEY);
    if (!v) { v = uid(); localStorage.setItem(VISITOR_KEY, v); }
    return v;
  } catch (_) { return uid(); }
};

const getSessionId = () => {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) { s = uid(); sessionStorage.setItem(SESSION_KEY, s); }
    return s;
  } catch (_) { return uid(); }
};

const detectDevice = () => {
  const ua = navigator.userAgent || "";
  if (/iPad|Tablet|PlayBook|Silk/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) return "tablet";
  if (/Mobi|Android|iPhone|iPod|Windows Phone/i.test(ua)) return "mobile";
  return "desktop";
};

const getUtm = () => {
  const out = {};
  const keys = ["source", "medium", "campaign", "term", "content"];
  try {
    const q = new URLSearchParams(window.location.search);
    keys.forEach((k) => {
      let v = q.get(`utm_${k}`) || "";
      if (v) { try { sessionStorage.setItem(`sl_utm_${k}`, v); } catch (_) { /* ignore */ } }
      if (!v) { try { v = sessionStorage.getItem(`sl_utm_${k}`) || ""; } catch (_) { /* ignore */ } }
      if (v) out[k] = v;
    });
    // No utm_source? Ad platforms append their own click IDs (Meta = fbclid,
    // Google = gclid, …) — in-app browsers strip referrers, so without this
    // paid ad traffic was landing in "direct".
    if (!out.source) {
      const CLICK_IDS = [["fbclid", "meta"], ["gclid", "google"], ["ttclid", "tiktok"], ["msclkid", "bing"]];
      for (const [param, src] of CLICK_IDS) {
        if (q.get(param)) {
          out.source = src;
          try { sessionStorage.setItem("sl_utm_source", src); } catch (_) { /* ignore */ }
          break;
        }
      }
    }
  } catch (_) { /* ignore */ }
  return out;
};

// Which part of the ecosystem a path belongs to — lets the admin split
// "landing page" (the /getleads ad-traffic funnel) from the main website.
const areaForPath = (path) => {
  const p = path || "/";
  if (p.startsWith("/getleads") || p.startsWith("/thanks") || p.startsWith("/rebook")) {
    return "landing_page";
  }
  return "main_website";
};

// The area the session STARTED in (landing_page vs main_website). Funnel
// conversion events (started search → details → booked/paid) happen on /scan,
// /audit, /kickoff (all "main_website" by path), so we attribute them to where
// the journey actually began — keeping the /getleads funnel and the main-site
// funnel correctly separated end-to-end. Stamped once per tab session.
const getOriginArea = () => {
  try {
    let a = sessionStorage.getItem(ORIGIN_KEY);
    if (!a) { a = areaForPath(window.location.pathname); sessionStorage.setItem(ORIGIN_KEY, a); }
    return a;
  } catch (_) { return areaForPath(window.location.pathname); }
};

let _page = "landing";
let queue = [];

const context = () => {
  const path = window.location.pathname || "/";
  return {
    session_id: getSessionId(),
    visitor_id: getVisitorId(),
    page: _page,
    path,
    area: areaForPath(path),
    referrer: document.referrer || "",
    device: detectDevice(),
    utm: getUtm(),
  };
};

const flush = (useBeacon = false) => {
  if (!queue.length) return;
  const events = queue;
  queue = [];
  const body = JSON.stringify({ ...context(), events });
  try {
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
      return;
    }
  } catch (_) { /* fall through to fetch */ }
  fetch(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => { /* analytics must never break the app */ });
};

const push = (type, label, areaOverride) => {
  const path = window.location.pathname;
  queue.push({ type, label: label || "", page: _page, path, area: areaOverride || areaForPath(path), ts: Date.now() });
  // small debounce so bursts (e.g. rapid clicks) batch into one request
  if (push._t) clearTimeout(push._t);
  push._t = setTimeout(() => flush(false), 800);
};

// ---- public API ----
export const trackPageView = () => push("page_view");
export const trackEvent = (label) => { push("event", label); flush(false); };
// Funnel conversion events — attributed to the session's ORIGIN area so the
// scan→report→book/pay journey lands in the right funnel regardless of path.
export const trackFunnel = (label) => { push("event", label, getOriginArea()); flush(false); };
const heartbeat = () => { push("heartbeat"); flush(false); };

// Emergent preview deployments (*.preview.emergentagent.com) must never track —
// testing there shouldn't pollute the live funnel numbers, the AI analysis, or
// Meta's ad optimization. Explicit ?sl_track=1 overrides (to test tracking itself).
// The same goes for the LIVE site when it's opened inside the Emergent app
// (app.emergent.sh embeds it in an iframe) — that's us, not a customer.
const EMERGENT_RX = /(^|\.)(emergent\.sh|emergentagent\.com)$/i;
const hostOf = (url) => { try { return new URL(url).hostname; } catch (_) { return ""; } };
export const isPreviewHost = () =>
  typeof window !== "undefined" && /(^|\.)preview\.emergentagent\.com$/.test(window.location.hostname);
export const isEmergentEmbed = () => {
  if (typeof window === "undefined") return false;
  if (EMERGENT_RX.test(hostOf(document.referrer || ""))) return true;
  try {
    const anc = window.location.ancestorOrigins;
    if (anc && Array.from(anc).some((o) => EMERGENT_RX.test(hostOf(o)))) return true;
  } catch (_) { /* ignore */ }
  return false;
};
export const trackingBlockedOnPreview = () => {
  if (!isPreviewHost() && !isEmergentEmbed()) return false;
  try { return new URLSearchParams(window.location.search).get("sl_track") !== "1"; } catch (_) { return true; }
};

let _inited = false;
export function initAnalytics(page = "landing") {
  // Do-not-track for the team's own devices: a persistent flag (set on admin
  // login, or via ?sl_notrack=1) keeps your own visits out of the funnel numbers
  // + AI analysis. Clear it with ?sl_track=1.
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.get("sl_track") === "1") localStorage.removeItem("sl_dnt");
    if (q.get("sl_notrack") === "1") localStorage.setItem("sl_dnt", "1");
  } catch (_) { /* ignore */ }
  try {
    if (localStorage.getItem("sl_dnt") === "1") return;
  } catch (_) { /* ignore */ }
  if (trackingBlockedOnPreview()) return;

  if (_inited) return;
  _inited = true;
  _page = page;
  getOriginArea(); // stamp the session's starting area once (for funnel attribution)

  trackPageView();

  // Delegated click capture: any element carrying a data-testid becomes a
  // ranked "what they click" entry (buttons, links, CTAs, etc.).
  document.addEventListener(
    "click",
    (e) => {
      try {
        const el = e.target?.closest?.("[data-testid]");
        if (el) push("click", el.getAttribute("data-testid"));
      } catch (_) { /* ignore */ }
    },
    { capture: true, passive: true }
  );

  // Heartbeat while the tab is visible -> powers "live now" + session duration.
  const hb = setInterval(() => {
    if (document.visibilityState === "visible") heartbeat();
  }, 15000);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
    else heartbeat();
  });
  window.addEventListener("pagehide", () => flush(true));
  window.addEventListener("beforeunload", () => flush(true));

  // one immediate heartbeat so a visitor shows up as live right away
  heartbeat();
  return () => clearInterval(hb);
}
